import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";

export default function FloatingActions() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Tooltip animation at intervals
  useEffect(() => {
    const showTooltipInterval = () => {
      setShowTooltip(true);
      setTimeout(() => {
        setShowTooltip(false);
      }, 3000); // Show for 3 seconds
    };

    // First show after 2 seconds
    const initialTimeout = setTimeout(showTooltipInterval, 2000);

    // Then show every 15 seconds
    const interval = setInterval(showTooltipInterval, 15000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="fixed bottom-6 right-6 z-[999] flex flex-col gap-3">
      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`w-14 h-14 bg-white dark:bg-[#2A2A2A] border-2 border-[#62397f] dark:border-[#A78BFA] rounded-full flex items-center justify-center text-[#62397f] dark:text-[#A78BFA] hover:bg-[#62397f] hover:text-white dark:hover:bg-[#A78BFA] dark:hover:text-black transition-all duration-300 shadow-[0_8px_24px_rgba(139,92,246,0.25)] hover:scale-110 ${showBackToTop
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4 pointer-events-none"
          }`}
        aria-label="Back to top"
      >
        <ArrowUp size={24} strokeWidth={2.5} />
      </button>

      {/* WhatsApp Contact Button with Tooltip */}
      <div className="relative">
        {/* Tooltip */}
        <div
          className={`absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap transition-all duration-300 ${showTooltip
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-4 pointer-events-none"
            }`}
        >
          <div className="relative bg-white dark:bg-[#2A2A2A] text-[#333333] dark:text-white px-4 py-2.5 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.15)] border border-[#E9E9E9] dark:border-[#444444]">
            <p className="text-sm font-semibold">💬 Chat with our team!</p>
            {/* Arrow */}
            <div className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-3 bg-white dark:bg-[#2A2A2A] border-r border-b border-[#E9E9E9] dark:border-[#444444] rotate-[-45deg]"></div>
          </div>
        </div>

        {/* WhatsApp Button */}
        <Link
          to="https://wa.me/2348023461907?text=Hello!%20I'm%20interested%20in%20learning%20more%20about%20your%20rental%20services."
          target="_blank"
          rel="noopener noreferrer"
          className="block w-14 h-14 bg-[#25D366] hover:bg-[#20BA5A] rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-[0_8px_24px_rgba(37,211,102,0.35)] hover:scale-110"
          aria-label="Contact us on WhatsApp"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>

          {/* Pulse Animation Ring */}
          <span
            className={`absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-75 ${showTooltip ? "" : "opacity-0"
              }`}
            style={{ animationDuration: "2s" }}
          ></span>
        </Link>
      </div>
    </div>
  );
}
